import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Calendar, Clock, LogOut, Plus, UserCircle } from 'lucide-react';
import DoctorProfile from '@/components/doctor/DoctorProfile';
import DoctorTimeSlots from '@/components/doctor/DoctorTimeSlots';
import DoctorAppointments from '@/components/doctor/DoctorAppointments';

export default function DoctorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('appointments');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const tabs = [
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'time-slots', label: 'Time Slots', icon: Clock },
    { id: 'profile', label: 'Profile', icon: UserCircle },
  ];

  return (
    <div className="min-h-screen bg-background-subtle">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 bg-primary rounded-full flex items-center justify-center">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold text-background-dark">Doctor Dashboard</h1>
              <p className="text-sm text-slate-600">Welcome, Dr. {user?.full_name}</p>
            </div>
          </div>
          <Button
            data-testid="doctor-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="text-slate-600 hover:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  data-testid={`doctor-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'text-primary border-primary'
                      : 'text-slate-600 border-transparent hover:text-primary'
                  }`}
                >
                  <Icon className="inline-block mr-2 h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {activeTab === 'appointments' && <DoctorAppointments />}
        {activeTab === 'time-slots' && <DoctorTimeSlots />}
        {activeTab === 'profile' && <DoctorProfile />}
      </main>
    </div>
  );
}